import React, { useMemo, useCallback } from 'react';
import { BoardingServiceItem, ServicesSectionConfig } from './types';
import { Service } from 'model/Service';
import { ServiceRow } from './ServiceRow';
import { Option } from '@spike/model';
import BoardingServiceMultipleSelector from '../BoardingServiceMultipleSelector';
import styles from './ServicesTable.module.scss';

interface ServicesTableProps {
    services: BoardingServiceItem[];
    availableServices: Service[];
    onChange: (services: BoardingServiceItem[]) => void;
    config: ServicesSectionConfig;
}

const ServicesTable: React.FC<ServicesTableProps> = ({ services, availableServices, onChange, config }) => {
    const handleServiceUpdate = (updatedService: BoardingServiceItem) => {
        onChange(services.map(s => (s.id === updatedService.id ? updatedService : s)));
    };

    const handleServiceRemove = (serviceId: string) => {
        onChange(services.filter(s => s.id !== serviceId));
    };

    const serviceOptions: Array<Option<number>> = useMemo(() => {
        return availableServices.map(service => ({
            id: service.id!,
            name: service.name
        }));
    }, [availableServices]);

    const selectedServiceIds = useMemo(() => {
        return services.map(s => s.serviceId).filter(id => id > 0);
    }, [services]);

    const allServicesSelected = services.length >= availableServices.length;

    const handleServiceSelection = useCallback(
        (newSelectedIds: Array<number>) => {
            const currentSelectedIds = services.map(s => s.serviceId).filter(id => id > 0);

            const addedIds = newSelectedIds.filter(id => !currentSelectedIds.includes(id));
            const removedIds = currentSelectedIds.filter(id => !newSelectedIds.includes(id));

            if (addedIds.length === 0 && removedIds.length === 0) {
                return;
            }

            let updatedServices = [...services];

            addedIds.forEach(serviceId => {
                const selectedService = availableServices.find(s => s.id === serviceId);
                if (selectedService) {
                    const standardRate = selectedService.pricing?.fixedPrice?.[0]?.price || 0;
                    updatedServices.push(config.createServiceItem(selectedService, standardRate));
                }
            });

            updatedServices = updatedServices.filter(s => !removedIds.includes(s.serviceId));

            onChange(updatedServices);
        },
        [services, availableServices, onChange, config]
    );

    return (
        <div
            className={styles.container}
            style={
                {
                    '--services-grid-columns': config.gridTemplateColumns,
                    '--services-row-mobile-display': config.showNewRateColumn ? 'grid' : 'none'
                } as React.CSSProperties
            }
        >
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    {config.columns.map(col => (
                        <div
                            key={col.key}
                            className={styles.headerCell}
                        >
                            {col.label}
                        </div>
                    ))}
                </div>
            </div>

            {services.length > 0 && (
                <div className={styles.rows}>
                    {services.map(service => (
                        <ServiceRow
                            key={service.id}
                            service={service}
                            onChange={handleServiceUpdate}
                            onRemove={handleServiceRemove}
                            config={config}
                        />
                    ))}
                </div>
            )}

            {!allServicesSelected && (
                <div className={styles.addButtonContainer}>
                    <BoardingServiceMultipleSelector
                        options={serviceOptions}
                        selectedIds={selectedServiceIds}
                        allOptionLabel="All Services"
                        enableSearch={true}
                        onChange={handleServiceSelection}
                    />
                </div>
            )}
        </div>
    );
};

export { ServicesTable };
export default ServicesTable;
